/**
 * @file index.tsx
 *
 * @fileoverview Renders a text with an image on the side based on the paragraph
 * information.
 */

import React from 'react';
import { TextWithImageOnSideParagraph } from '~/jobiqo/types/types';
import Link from '~/jobiqo/components/Link';
import { getFormattedPageLink } from '~/jobiqo/utils/routes';
import { Main } from '~/vcl/atoms/06-layout/09-main';
import { Box } from '~/vcl/atoms/07-box';
import { SideText } from '~/vcl/organisms/04-landingpage/04-sidetext';
import { LinkButton } from '~/vcl/atoms/01-forms/01-button/02-link-button';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { WYSIWYGText } from '~/vcl/atoms/13-text/02-WYSIWYG';
import styles from './textWithImageOnSide.module.scss';
import { getButtonStyleProps } from '../utils/buttonStyles';
import NextLink from 'next/link';

export interface TextWithImageOnSideParagraphProps {
  rowSideText: TextWithImageOnSideParagraph;
  maxContainerSize?: 'small' | 'medium' | 'large' | 'xLarge';
}

export const TextWithImageOnSide = ({
  rowSideText,
  maxContainerSize,
}: TextWithImageOnSideParagraphProps) => {
  const { alt, title, url, width, height, caption } =
    rowSideText.featureElementImage ?? {};

  return (
    <Box
      boxShadow="none"
      borderRadius="0px"
      margin={0}
      paddingTop={rowSideText.spacing ?? 9}
      paddingBottom={rowSideText.spacing ?? 9}
      paddingRight={0}
      paddingLeft={0}
      className={`jd-lp-text-with-image-on-side jd-lp-text-with-image-on-side__${
        rowSideText.featureElementDirection
      }-${rowSideText?.background}-${
        rowSideText?.featureElementDarkBg ? 'dark' : 'light'
      }`}
      backgroundColorOverride={
        rowSideText.background
          ? `var(--color-${rowSideText.background})`
          : 'transparent'
      }
    >
      <Main {...(maxContainerSize ? { size: maxContainerSize } : null)}>
        <SideText
          {...(rowSideText.featureElementDirection === 'right'
            ? { imageOrientation: 'right' }
            : null)}
          image={{
            url: url ?? '',
            alt: alt ?? '',
            title: title ?? '',
            width: width?.toString(),
            height: height?.toString(),
          }}
          {...(caption && { caption })}
        >
          <div
            className={
              rowSideText?.featureElementDarkBg
                ? styles.darkText
                : styles.lightText
            }
          >
            <WYSIWYGText
              format="full_html"
              content={
                rowSideText.featureElementText
                  ? rowSideText.featureElementText
                  : ''
              }
            />
          </div>
          {rowSideText.featureElementLink &&
            rowSideText.featureElementLink?.length > 0 && (
              <Spacing marginTop={7}>
                <FlexItems gap={5}>
                  {rowSideText.featureElementLink.map((link) => (
                    <FlexItems
                      key={link?.url}
                      justifyContent={['center', 'flex-start']}
                    >
                      <Link prefetch={false} asChild>
                        <LinkButton
                          linkElement={NextLink}
                          {...getButtonStyleProps(link?.linkStyle ?? '')}
                          {...getFormattedPageLink(link?.url ?? '')}
                        >
                          {link?.text}
                        </LinkButton>
                      </Link>
                    </FlexItems>
                  ))}
                </FlexItems>
              </Spacing>
            )}
        </SideText>
      </Main>
    </Box>
  );
};
export default TextWithImageOnSide;
